//
// Copyright (c) 2011-2024 by Curalate, Inc.
//

import { theme } from '@bvengineering/unison-core';
import * as ScrollArea from '@radix-ui/react-scroll-area';
import * as RadixSelect from '@radix-ui/react-select';
import classNames from 'classnames';
import React, { ReactNode } from 'react';
import styled, { css } from 'styled-components';

import Icon from '@components/atoms/icon/Icon';
import { Mixins } from '@theme/mixins';
import { withTracking } from '@utils/withTracking';

/**
 * `Select` is a flexible and accessible component, capable of customization options such as triggers, labels, groups, and items.
 *
 * Note: When using custom components as triggers or within the menu, it's crucial to apply the `forwardRef` pattern
 * to maintain functionality. Failure to do so may result in unresponsive or incorrectly functioning dropdowns, leading
 * to potential confusion about the component's behavior.
 *
 * For example, if you want to use our buttons from the atoms folder we can consider using them like:
 * <Button buttonStyles={DefaultButtonStyles}>Label</Button>
 *
 * @example
 * This example showcases the `Select` with custom trigger and different options.
 *
 * <Select value={selected} onValueChange={setSelected}>
 *   <Select.Trigger asChild>
 *     <Button buttonStyles={DefaultButtonStyles} isDropdown={true}>
 *       <Select.Value placeholder="Select value" />
 *     </Button>
 *   </Select.Trigger>
 *   <Select.Content align='start'>
 *     {basicDropdownOptions.map((option) => (
 *       <Select.Item
 *         key={option.label}
 *         value={option.value}
 *         onSelect={() => setSelected(option.value)}
 *       >
 *         <Select.ItemText>
 *           {option.label}
 *         </Select.ItemText>
 *         <Select.Indicator />
 *       </Select.Item>
 *     ))}
 *   </Select.Content>
 *  </Select>
 */
export const Select = (props: RadixSelect.SelectProps) => {
  return <RadixSelect.Root {...props} />;
};

const baseContentWrapperStyle = () => css`
  padding: ${theme.spacings[2]} ${theme.spacings[0]};
  box-shadow: ${theme.boxShadows[3]};
  border: ${theme.borderWidths[1]} solid ${theme.colors.gray3};
  background-color: ${theme.colors.white};
  max-height: var(--radix-select-content-available-height);
`;

const Content = styled(RadixSelect.Content)(baseContentWrapperStyle);

export interface SelectContentProps extends RadixSelect.SelectContentProps {
  children: ReactNode;
  /**
   * Optional HTML portal container element to be used as the portal parent.
   * In some cases, the Radix-based portal will not work in conjunction with other components.
   * The solution for this issue is to use a parent container as a static element.
   */
  portalContainer?: HTMLElement;
  className?: string;
  onClose?: () => void;
}

const SelectContent = ({
  children,
  className,
  onClose,
  portalContainer,
  ...contentProps
}: SelectContentProps) => (
  <RadixSelect.Portal container={portalContainer}>
    <Content
      className={classNames('unison-component', className)}
      onEscapeKeyDown={onClose}
      onPointerDownOutside={onClose}
      position='popper'
      {...contentProps}
    >
      <ScrollArea.Root type='auto'>
        <RadixSelect.Viewport asChild>
          <ScrollArea.Viewport>{children}</ScrollArea.Viewport>
        </RadixSelect.Viewport>
        <ScrollArea.Scrollbar orientation='vertical' />
      </ScrollArea.Root>
    </Content>
  </RadixSelect.Portal>
);

const baseSelectStyle = () => css`
  ${Mixins.Text.UiCopy}
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: calc(${theme.spacings[2]} + ${theme.spacings[1]});
  cursor: pointer;

  &:hover {
    background-color: ${theme.colors.gray0};
    text-decoration: underline;
  }

  &:focus {
    background-color: ${theme.colors.gray0};
    outline: none;
  }

  &:focus-visible {
    outline: inherit;
  }

  &[data-disabled] {
    color: ${theme.colors.gray4};
    background-color: ${theme.colors.gray0};
    border-color: ${theme.colors.gray0};
    cursor: default;

    &:hover {
      text-decoration: none;
    }
  }
`;

const selectIndicatorStyle = () => css`
  ${Mixins.Text.UiCopy}
  padding-left: ${theme.spacings[3]};
`;

const SelectLabel = styled(RadixSelect.Label)(baseSelectStyle);

const SelectItem = styled(RadixSelect.Item)(baseSelectStyle);

const ItemIndicator = styled(RadixSelect.ItemIndicator)(selectIndicatorStyle);

const ItemText = styled(RadixSelect.ItemText)(baseSelectStyle);

const SelectIndicatorIcon = styled(Icon).attrs({
  name: 'checkmark',
})(
  ({ theme }) => css`
    color: ${theme.colors.green7};
  `,
);

const SelectIndicator = (props: RadixSelect.SelectItemIndicatorProps) => (
  <ItemIndicator {...props}>
    <SelectIndicatorIcon />
  </ItemIndicator>
);

const SelectIcon = (props: RadixSelect.SelectIconProps) => (
  <RadixSelect.Icon {...props}>
    <Icon name='chevron-down' />
  </RadixSelect.Icon>
);

const ValueWrapper = styled.div(
  ({ theme }) => `
    display: flex;
    align-items: center;
    gap: ${theme.spacings[1]}
  `,
);

export interface ValueProps extends RadixSelect.SelectValueProps {
  isCheckmarkVisible?: boolean;
}

const Value = ({ isCheckmarkVisible, ...props }: ValueProps) =>
  isCheckmarkVisible ? (
    <ValueWrapper>
      <SelectIndicatorIcon />
      <RadixSelect.Value {...props} />
    </ValueWrapper>
  ) : (
    <RadixSelect.Value {...props} />
  );

Select.Content = SelectContent;
Select.Group = RadixSelect.Group;
Select.Indicator = SelectIndicator;
Select.Icon = SelectIcon;
Select.Item = withTracking(SelectItem);
Select.ItemIndicator = RadixSelect.ItemIndicator;
Select.ItemIndicatorIcon = SelectIndicatorIcon;
Select.ItemText = ItemText;
Select.Label = SelectLabel;
Select.Separator = RadixSelect.Separator;
Select.Trigger = withTracking(RadixSelect.Trigger);
Select.Value = Value;
